import React, { useState, useEffect, useRef } from "react";
import { Collapse, Container } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import classname from "classnames";
import { withTranslation } from "react-i18next";
import { DESIGNATION_PERMISSIONS } from "../../config/designationPermissions";

const Navbar = ({ t, menuOpen, toggleMenu }) => {
    const [activeLink, setActiveLink] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);
    const navRef = useRef(null);
    const location = useLocation();

    // const toggleMenu = () => {
    //     setMenuOpen(!menuOpen);
    // };

    const shouldShowModule = (moduleName) => {
        if (!userPermissions) return false;
        if (userPermissions.modules.includes('all')) return true;
        return userPermissions.modules.includes(moduleName) && 
               !userPermissions.restrictions.includes(moduleName);
    };

    // Close all menus including mobile menu
    const closeAllMenus = () => {
        setActiveDropdown(null);
        if (menuOpen && typeof toggleMenu === 'function') {
            toggleMenu();
        }
    };

    // Handle link click - update active link and close menus
    const handleLinkClick = (e) => {
        setActiveLink(e.currentTarget);
        closeAllMenus();
    };

    // Toggle dropdown visibility
    const toggleDropdown = (dropdownName, e) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleDocumentClick = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, []);

    // Set user permissions and active link
    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user'));
        const designation = userData?.designation;
        
        if (designation && DESIGNATION_PERMISSIONS[designation]) {
            setUserPermissions(DESIGNATION_PERMISSIONS[designation]);
        } else {
            setUserPermissions({
                modules: [],
                restrictions: ['all']
            });
        }

        // Set active link based on current route
        if (navRef.current) {
            const links = navRef.current.querySelectorAll("a");
            links.forEach((link) => {
                if (link.pathname === location.pathname) {
                    setActiveLink(link);
                }
            });
        }
    }, [location]);

    if (!userPermissions) {
        return <div className="topnav"></div>;
    }

    return (
        <React.Fragment>
            <div className="topnav">
                <Container fluid>
                    <nav className="navbar navbar-light navbar-expand-lg topnav-menu" ref={navRef} toggleMenu={toggleMenu}>
                        <Collapse 
                            isOpen={menuOpen} 
                            className="navbar-collapse" 
                            id="topnav-menu-content"
                            navbar
                        >
                            <ul className="navbar-nav">
                                <li className="nav-item">
                                    <Link
                                        className={classname("nav-link", { active: activeLink?.pathname === "/" })}
                                        to="/"
                                        onClick={handleLinkClick}
                                    >
                                        <i className="ri-dashboard-line me-2"></i> {t('Dashboard')}
                                    </Link>
                                </li>

                                {/* Sales & Leads */}
                                {shouldShowModule('sales') && (
                                <li className="nav-item dropdown">
                                    <Link
                                        className={classname("nav-link dropdown-toggle arrow-none", {
                                            active: activeLink?.pathname.startsWith("/staff/salesandleads"),
                                        })}
                                        to="#"
                                        onClick={(e) => toggleDropdown("salesAndLeads", e)}
                                    >
                                        <i className="ri-bank-line me-2"></i>
                                        {t("Sales & Leads")} <div className="arrow-down"></div>
                                    </Link>
                                    <div
                                        className={classname("dropdown-menu", {
                                            show: activeDropdown === "salesAndLeads",
                                        })}
                                    >
                                        <Link to="/staff/salesandleads/leads" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Leads")}
                                        </Link>
                                        <Link to="/staff/salesandleads/clients" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Clients")}
                                        </Link>
                                    </div>
                                </li>
                                )}

                                {/* Project */}
                                {shouldShowModule('projects') && (
                                <li className="nav-item dropdown">
                                    <Link
                                        className={classname("nav-link dropdown-toggle arrow-none", {
                                            active: activeLink?.pathname.startsWith("/staff/projectsandtasks"),
                                        })}
                                        to="#"
                                        onClick={(e) => toggleDropdown("projectsAndTasks", e)}
                                    >
                                        <i className="ri-bank-line me-2"></i>
                                        {t("Projects & Task")} <div className="arrow-down"></div>
                                    </Link>
                                    <div
                                        className={classname("dropdown-menu", {
                                            show: activeDropdown === "projectsAndTasks",
                                        })}
                                    >
                                        <Link to="/staff/projectsandtasks/projects" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Projects")}
                                        </Link>
                                        <Link to="/staff/projectsandtasks/tasks" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Tasks")}
                                        </Link>
                                        {shouldShowModule('schedule_tasks') && (
                                        <Link to="/staff/projectsandtasks/scheduledtasks" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Scheduled Tasks")}
                                        </Link>
                                        )}
                                    </div>
                                </li>
                                )}

                                {/* Work flow */}
                                {shouldShowModule('workflow') && (
                                <li className="nav-item dropdown">
                                    <Link
                                        className={classname("nav-link dropdown-toggle arrow-none", {
                                            active: activeLink?.pathname.startsWith("/staff/workflow"),
                                        })}
                                        to="#"
                                        onClick={(e) => toggleDropdown("workflow", e)}
                                    >
                                        <i className="ri-bank-line me-2"></i>
                                        {t("Workflow")} <div className="arrow-down"></div>
                                    </Link>
                                    <div
                                        className={classname("dropdown-menu mega-dropdown-menu px-2 dropdown-mega-menu-xl", {
                                            show: activeDropdown === "workflow",
                                        })}
                                        style={{ width: "auto", maxWidth: "300px", whiteSpace: "nowrap" }}
                                    >
                                        <Link to="/staff/workflow/team-chat" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Team Chat")}
                                        </Link>
                                        <Link to="/staff/workflow/standup" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Daily Stand up Meeting")}
                                        </Link>
                                    </div>
                                </li>
                                )}

                                {/* Accounts */}
                                {!userPermissions.restrictions.includes('accounts') && (
                                <li className="nav-item dropdown">
                                    <Link
                                        className={classname("nav-link dropdown-toggle arrow-none", { 
                                            active: activeLink?.pathname.startsWith("/staff/finance") 
                                        })}
                                        to="#"
                                        onClick={(e) => toggleDropdown("accounts", e)}
                                    >
                                        <i className="ri-numbers-line me-2"></i>{t('Accounts')} <div className="arrow-down"></div>
                                    </Link>
                                    <div
                                        className={classname("dropdown-menu mega-dropdown-menu px-2 dropdown-mega-menu-xl", {
                                            show: activeDropdown === "accounts",
                                        })}
                                        style={{ width: "auto", maxWidth: "300px", whiteSpace: "nowrap" }}
                                    >
                                        <Link to="/admin/accounts/invoice" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Invoices")}
                                        </Link>
                                        <Link to="/staff/finance/expenses" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Expenses")}
                                        </Link>
                                    </div>
                                </li>
                                )}

                                {/* Reports */}
                                {shouldShowModule('reports') && (
                                <li className="nav-item dropdown">
                                    <Link
                                        className={classname("nav-link dropdown-toggle arrow-none", {
                                            active: activeLink?.pathname.startsWith("/staff/reports"),
                                        })}
                                        to="#"
                                        onClick={(e) => toggleDropdown("reports", e)}
                                    >
                                        <i className="ri-bank-line me-2"></i>
                                        {t("Reports")} <div className="arrow-down"></div>
                                    </Link>
                                    <div
                                        className={classname("dropdown-menu mega-dropdown-menu px-2 dropdown-mega-menu-xl", {
                                            show: activeDropdown === "reports",
                                        })}
                                        style={{ width: "auto", maxWidth: "300px", whiteSpace: "nowrap" }}
                                    >
                                        <Link to="/staff/reports/projects" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Projects")}
                                        </Link>
                                        <Link to="/staff/reports/progress" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Real Time Progress")}
                                        </Link>
                                    </div>
                                </li>
                                )}

                                {/* Office */}
                                {!userPermissions.restrictions.includes('office') && (
                                <li className="nav-item dropdown">
                                    <Link
                                        className={classname("nav-link dropdown-toggle arrow-none", {
                                            active: activeLink?.pathname.startsWith("/staff/office"),
                                        })}
                                        to="#"
                                        onClick={(e) => toggleDropdown("office", e)}
                                    >
                                        <i className="ri-settings-2-line me-2"></i>
                                        {t("Office")} <div className="arrow-down"></div>
                                    </Link>
                                    <div
                                        className={classname("dropdown-menu mega-dropdown-menu px-2 dropdown-mega-menu-xl", {
                                            show: activeDropdown === "office",
                                        })}
                                        style={{ width: "auto", maxWidth: "300px", whiteSpace: "nowrap" }}
                                    >
                                        <Link to="/staff/office/departments" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Departments")}
                                        </Link>
                                        <Link to="/staff/office/designations" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Designations")}
                                        </Link>
                                        <Link to="/admin/office/attendance" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Attendance")}
                                        </Link>
                                        <Link to="/admin/office/attendance" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Attendance Report")}
                                        </Link>
                                        <Link to="/staff/office/website/blogs" className="dropdown-item" onClick={handleLinkClick}>
                                            {t("Blog")}
                                        </Link>
                                    </div>
                                </li>
                                )}
                            </ul>
                        </Collapse>
                    </nav>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default withTranslation()(Navbar);